import { useCallback } from "react";

import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";

import { airHandlerUnitApi } from "~/api";
import { Mode } from "~/components/live-history-toggle";
import { AirHandlerUnitProvider } from "~/features/air-handler-unit";
import { requirePagePermission } from "~/helpers/auth/route-permission.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import {
  isSameHistoryDate,
  planHistorySearchSchema,
  toPlanHistorySearch,
  type HistoryDateChangeOptions,
} from "~/helpers/history-search.helper";
import { m } from "~/paraglide/messages";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/air-handler-units/$airHandlerUnitId")({
  validateSearch: planHistorySearchSchema,
  beforeLoad: ({ context: { permissions }, params: { buildingId }, preload }) => {
    requirePagePermission(permissions, "airHandlerUnit", buildingId, preload);
  },
  loader: async ({ context: { queryClient }, params: { buildingId } }) => {
    const airHandlerUnits = await queryClient.ensureQueryData(airHandlerUnitApi.queries.building(buildingId));
    await Promise.all(
      airHandlerUnits.map(airHandlerUnit =>
        queryClient.ensureQueryData(airHandlerUnitApi.queries.airHandlerUnit(airHandlerUnit.id)),
      ),
    );
  },
  head: async ({ match: { context }, params: { buildingId, airHandlerUnitId } }) => {
    const airHandlerUnits = await context.queryClient.ensureQueryData(airHandlerUnitApi.queries.building(buildingId));
    return buildingPageHead(
      context.queryClient,
      buildingId,
      airHandlerUnits.find(airHandlerUnit => airHandlerUnit.id === airHandlerUnitId)?.name ?? m.air_handler_units(),
    );
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { buildingId, airHandlerUnitId } = Route.useParams();
  const { mode, date } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: airHandlerUnits } = useSuspenseQuery(airHandlerUnitApi.queries.building(buildingId));
  const airHandlerUnitsQueries = useSuspenseQueries({
    queries: airHandlerUnits.map(airHandlerUnit => airHandlerUnitApi.queries.airHandlerUnit(airHandlerUnit.id)),
  });
  const fullAirHandlerUnits = airHandlerUnitsQueries.map(query => query.data);

  const handleModeChange = useCallback(
    async (nextMode: Mode) => {
      await navigate({
        search: toPlanHistorySearch(nextMode, nextMode === Mode.History ? date : undefined),
        resetScroll: false,
      });
    },
    [date, navigate],
  );

  const handleDateChange = useCallback(
    async (nextDate: string, options?: HistoryDateChangeOptions) => {
      await navigate({
        search: prev => {
          if (isSameHistoryDate(prev.date, nextDate) && prev.mode === Mode.History) {
            return prev;
          }
          return toPlanHistorySearch(Mode.History, nextDate);
        },
        replace: options?.replace,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const onModeChange = (nextMode: Mode) => {
    void handleModeChange(nextMode);
  };

  const onDateChange = (nextDate: string, options?: HistoryDateChangeOptions) => {
    void handleDateChange(nextDate, options);
  };

  return (
    <AirHandlerUnitProvider
      airHandlerUnits={fullAirHandlerUnits}
      airHandlerUnitId={airHandlerUnitId}
      mode={mode}
      historyDate={date}
      onModeChange={onModeChange}
      onDateChange={onDateChange}
    >
      <Outlet />
    </AirHandlerUnitProvider>
  );
}
